import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, query } from 'firebase/firestore';
import { useAuth } from './contexts/AuthContext';
import { appId, GOOGLE_CLIENT_ID, isFirebaseConfigured } from './config/env';
import { db } from './config/firebase';
import { createCalendarTokenClient, loadGoogleIdentityScript } from './services/googleCalendar';
import { subscribeCalendarEvents } from './services/calendarStore';
import LoginScreen from './components/auth/LoginScreen';
import ConfigurationNeeded from './components/layout/ConfigurationNeeded';
import Sidebar from './features/sidebar/Sidebar';
import Dashboard from './features/dashboard/Dashboard';
import ProjectDetail from './features/project/ProjectDetail';
import AllTasksView from './features/tasks/AllTasksView';
import ScheduleView from './features/schedule/ScheduleView';
import HabitTrackerView from './features/habits/HabitTrackerView';
import WeeklyReviewView from './features/review/WeeklyReviewView';
import LandingPage from './components/layout/LandingPage';
import PomodoroTimer from './features/pomodoro/PomodoroTimer';
import CommandPalette from './features/search/CommandPalette';
import { Menu, Book, Search } from 'lucide-react';

// Safety caps on per-user reads. Generous for a personal productivity app, but
// they bound worst-case Firestore reads instead of subscribing to whole
// collections unbounded. (Bare limits don't drop docs missing a field.)
const READ_LIMITS = {
    projects: 500,
    tasks: 2000,
    habits: 200,
    goals: 200,
    habitEntries: 5000,
};

function HubApp({ user, handleSignOut }) {
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [habits, setHabits] = useState([]);
    const [goals, setGoals] = useState([]);
    const [habitEntries, setHabitEntries] = useState([]);
    const [activeView, setActiveView] = useState('dashboard');
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isPaletteOpen, setIsPaletteOpen] = useState(false);

    const [syncedEvents, setSyncedEvents] = useState([]);
    const [lastSyncedAt, setLastSyncedAt] = useState(null);
    const [tokenClient, setTokenClient] = useState(null);
    const [isGsiScriptLoaded, setIsGsiScriptLoaded] = useState(false);

    // Rehydrate previously-synced calendar events from Firestore on load.
    useEffect(() => {
        if (!user) return undefined;
        const unsubscribe = subscribeCalendarEvents(user.uid, ({ events, syncedAt }) => {
            setSyncedEvents(events);
            setLastSyncedAt(syncedAt);
        });
        return unsubscribe;
    }, [user]);

    // Ctrl/Cmd+K opens the command palette
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setIsPaletteOpen((open) => !open);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        let cancelled = false;
        loadGoogleIdentityScript()
            .then(() => {
                if (!cancelled) setIsGsiScriptLoaded(true);
            })
            .catch((err) => console.error('Google Identity script failed to load', err));
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (isGsiScriptLoaded && window.google && GOOGLE_CLIENT_ID) {
            const client = createCalendarTokenClient({
                clientId: GOOGLE_CLIENT_ID,
                onToken: () => {},
                onError: (err) => console.error(err),
            });
            setTokenClient(client);
        }
    }, [isGsiScriptLoaded]);

    useEffect(() => {
        if (!user || !db) return undefined;
        const basePath = `artifacts/${appId}/users/${user.uid}`;

        const projectsQuery = query(collection(db, `${basePath}/projects`), limit(READ_LIMITS.projects));
        const unsubscribeProjects = onSnapshot(projectsQuery, (snapshot) => {
            const projectsData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            setProjects(projectsData);
            if (selectedProjectId && !snapshot.docs.some((docSnap) => docSnap.id === selectedProjectId)) {
                setActiveView('dashboard');
                setSelectedProjectId(null);
            }
        });

        const tasksQuery = query(collection(db, `${basePath}/tasks`), limit(READ_LIMITS.tasks));
        const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) =>
            setTasks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        const habitsQuery = query(collection(db, `${basePath}/habits`), limit(READ_LIMITS.habits));
        const unsubscribeHabits = onSnapshot(habitsQuery, (snapshot) =>
            setHabits(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        const goalsQuery = query(collection(db, `${basePath}/goals`), limit(READ_LIMITS.goals));
        const unsubscribeGoals = onSnapshot(goalsQuery, (snapshot) =>
            setGoals(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        const habitEntriesQuery = query(collection(db, `${basePath}/habit_entries`), limit(READ_LIMITS.habitEntries));
        const unsubscribeHabitEntries = onSnapshot(habitEntriesQuery, (snapshot) =>
            setHabitEntries(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        return () => {
            unsubscribeProjects();
            unsubscribeTasks();
            unsubscribeHabits();
            unsubscribeGoals();
            unsubscribeHabitEntries();
        };
    }, [user, selectedProjectId]);

    const selectedProject = useMemo(
        () => projects.find((p) => p.id === selectedProjectId),
        [selectedProjectId, projects]
    );

    const handleSetView = (view, projectId = null) => {
        setActiveView(view);
        setSelectedProjectId(projectId);
        setIsSidebarOpen(false); // Auto-close sidebar on mobile
    };

    return (
        <div className="bg-gray-900 text-gray-100 min-h-screen font-sans flex flex-col md:flex-row">
            {/* Mobile Header */}
            <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-700/50 bg-gray-900">
                <h1 className="text-xl font-bold text-blue-400 flex items-center gap-2">
                    <Book size={20} /> ProdHub
                </h1>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setIsPaletteOpen(true)}
                        aria-label="Search"
                        className="text-gray-300 hover:text-white p-2 rounded-md hover:bg-gray-800 transition-colors"
                    >
                        <Search size={22} />
                    </button>
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={isSidebarOpen}
                        className="text-gray-300 hover:text-white p-2 rounded-md hover:bg-gray-800 transition-colors"
                    >
                        <Menu size={24} />
                    </button>
                </div>
            </div>

            {/* Sidebar Container */}
            <div className={`${isSidebarOpen ? 'block' : 'hidden'} md:block md:w-64 flex-shrink-0 h-[calc(100vh-73px)] md:h-screen w-full`}>
                <Sidebar
                    onViewChange={handleSetView}
                    projects={projects}
                    goals={goals}
                    userId={user.uid}
                    handleSignOut={handleSignOut}
                    onOpenSearch={() => setIsPaletteOpen(true)}
                />
            </div>
            
            <main className={`${isSidebarOpen ? 'hidden md:block' : 'block'} flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto max-h-screen flex flex-col`}>
                <div className="flex-1">
                    {activeView === 'dashboard' && (
                        <Dashboard
                            projects={projects}
                            tasks={tasks}
                            goals={goals}
                            onViewChange={handleSetView}
                            syncedEvents={syncedEvents}
                        />
                    )}
                    {activeView === 'project' && selectedProject && (
                        <ProjectDetail project={selectedProject} allTasks={tasks} syncedEvents={syncedEvents} />
                    )}
                    {activeView === 'all_tasks' && <AllTasksView tasks={tasks} projects={projects} />}
                    {activeView === 'schedule' && (
                        <ScheduleView
                            projects={projects}
                            tasks={tasks}
                            syncedEvents={syncedEvents}
                            setSyncedEvents={setSyncedEvents}
                            tokenClient={tokenClient}
                            lastSyncedAt={lastSyncedAt}
                        />
                    )}
                    {activeView === 'habit_tracker' && <HabitTrackerView habits={habits} entries={habitEntries} />}
                    {activeView === 'weekly_review' && (
                        <WeeklyReviewView tasks={tasks} projects={projects} habits={habits} entries={habitEntries} />
                    )}
                </div>
                <footer className="mt-8 pt-4 border-t border-gray-800 text-center text-xs text-gray-600 space-x-4">
                    <a href="https://my-productivity-hub-5a3ba.web.app/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400 transition-colors">Privacy Policy</a>
                    <span>·</span>
                    <a href="https://my-productivity-hub-5a3ba.web.app/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400 transition-colors">Terms of Service</a>
                </footer>
            </main>
            <PomodoroTimer />
            <CommandPalette
                isOpen={isPaletteOpen}
                onClose={() => setIsPaletteOpen(false)}
                projects={projects}
                tasks={tasks}
                onNavigate={handleSetView}
                defaultProjectId={selectedProjectId}
            />
        </div>
    );
}

export default function App() {
    const { user, isAuthReady, signInWithGoogle, emailSignIn, emailSignUp, signOutUser } = useAuth();
    const [hash, setHash] = useState(window.location.hash);

    useEffect(() => {
        const handleHashChange = () => setHash(window.location.hash);
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const navigate = (newHash) => {
        window.location.hash = newHash;
    };

    if (!isFirebaseConfigured || !GOOGLE_CLIENT_ID) {
        return <ConfigurationNeeded missingFirebase={!isFirebaseConfigured} missingGoogle={!GOOGLE_CLIENT_ID} />;
    }

    if (!isAuthReady) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (user) {
        // If the user logs in and is lingering on a landing/login route, push to main app
        if (hash === '#login') {
            window.location.hash = ''; // Clear hash for cleanly showing the app
        }
        return <HubApp user={user} handleSignOut={signOutUser} />;
    }

    // Unauthenticated User Routing
    if (hash === '#login') {
        return (
            <LoginScreen
                onGoogleSignIn={signInWithGoogle}
                onEmailSignIn={emailSignIn}
                onEmailSignUp={emailSignUp}
                onBack={() => navigate('')}
            />
        );
    }

    return <LandingPage />;
}
